import {Component, Inject, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {ProjectId, TestObjectCollectionId, TestObjectId} from '@webmate-web-core/shared/types/ids';
import {
    CreateTestObjectDTO,
    CrawledSourceSpec,
    ManualGenerationSpec,
    MobileAppGenerationSpec,
    NoAutomaticMatchingSpec,
    TestObject,
    TestObjectCollection,
    TestObjectCollectionMemberRef,
    TestObjectCollectionSourceSpec,
    TestObjectCollectionSummary,
    TestObjectListSourceSpec,
    TestObjectsSourceSpec,
    URLMatchingSpec,
    URLListSourceSpec
} from '@webmate-web-core/shared/types/testmgmt';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {getTestObjectCanonicalIdentifier} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-overview.utils';
import {getTestObjectCollectionMemberBaseId, getTestObjectCollectionMemberLabel} from '@webmate-web-core/shared/components/testmgmt/splitview/testobject-collections.utils';
import {forkJoin, of} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {PackageService} from '@webmate-web-core/shared/services/package.service';
import {VirtualPackage} from '@webmate-web-core/shared/types/packages/package';
import {AppUploadToolComponent, AppUploadToolConfig} from '@webmate-web-core/shared/tools/app-upload/app-upload-tool.component';

export type TestObjectsSourceType = 'urllist' | 'testobjectlist' | 'crawled' | 'testobjectcollection' | 'manual' | 'mobileapp';
export type PostCreatePolicyConfigurationMode = 'none' | 'configureNew';

export interface CreateTestObjectsSourceResult {
    directlyCreatedTestObjectIds?: TestObjectId[];
}

interface TestObjectOption {
    label: string;
    value: string;
    secondary?: string;
}

interface TestObjectCollectionOption {
    label: string;
    value: string;
    secondary: string;
    previewSummary?: string;
    memberLabels?: string[];
}

const MAX_COLLECTION_OPTION_PREVIEW_MEMBERS = 3;
const MAX_SELECTED_COLLECTION_VISIBLE_MEMBERS = 12;

export class CreateTestObjectsSourceToolConfig implements WebmateToolConfig {
    scrollable = true;

    constructor(public toolName: string = 'Create Test Object Source',
                public projectId: ProjectId,
                public onOk: (sourceSpec: TestObjectsSourceSpec, result?: CreateTestObjectsSourceResult) => void,
                public lastShownTool?: TestObjectsSourceType,
                public postCreatePolicyConfiguration: PostCreatePolicyConfigurationMode = 'none') {
    }
}

@Component({
    selector: 'wm-createtestobjects-source',
    templateUrl: './create-testobjects-source.component.html',
    styleUrls: ['./create-testobjects-source.component.less']
})
export class CreateTestObjectsSourceComponent implements OnInit {
    form: UntypedFormGroup;
    testObjectOptions: TestObjectOption[] = [];
    testObjectCollectionOptions: TestObjectCollectionOption[] = [];
    loadingTestObjectCollectionMembers: boolean = false;
    showAllSelectedCollectionMembers: boolean = false;
    confirming: boolean = false;
    manualLabels: Tag[] = [];
    mobilePackages: VirtualPackage[] = [];
    mobilePackageGroups: Array<{label: string, items: VirtualPackage[]}> = [];

    private activeTestObjectCollections: TestObjectCollectionSummary[] = [];
    private readonly knownTestObjectsById: Record<string, TestObject> = {};
    private readonly loadedTestObjectCollectionsById: Record<string, TestObjectCollection> = {};

    sourceOptions = [
        {label: 'URL List', value: 'urllist'},
        {label: 'Mobile App', value: 'mobileapp'},
        {label: 'Crawled Source', value: 'crawled'},
        {label: 'Known Test Objects', value: 'testobjectlist'},
        {label: 'Test Object Collection', value: 'testobjectcollection'},
        {label: 'Manual Test Object', value: 'manual'},
    ];
    crawlPrioritizingOptions = [
        {label: 'Breadth First', value: 'BreadthFirst'},
        {label: 'Depth First', value: 'DepthFirst'},
    ];
    manualMatchingModeOptions = [
        {label: 'Manual Only', value: 'manualOnly'},
        {label: 'Exact URL', value: 'exactUrl'},
        {label: 'URL Regex', value: 'urlRegex'},
    ];

    constructor(@Inject(TOOL_CONTAINER_DATA) public config: CreateTestObjectsSourceToolConfig,
                private fb: FormBuilder,
                private testService: TestService,
                private toolService: ModalToolContainerService,
                private notificationService: WmNotificationService,
                private packageService: PackageService) {
    }

    ngOnInit() {
        this.reset();
        this.loadKnownTestObjects();
        this.loadActiveTestObjectCollections();
        this.loadMobilePackages();
    }

    get urls(): FormArray {
        return (this.form?.get('urls') as FormArray) || this.fb.array([]);
    }

    get seedUrls(): FormArray {
        return (this.form?.get('seedUrls') as FormArray) || this.fb.array([]);
    }

    prependHttpsToUrl(index: number): void {
        const urlControl = this.urls.at(index).get('url');
        if (urlControl && typeof urlControl.value === 'string') {
            const trimmedUrl = urlControl.value.trim();
            urlControl.setValue(trimmedUrl);

            if (!(trimmedUrl.indexOf('.') === -1) && !trimmedUrl.startsWith('http')) {
                urlControl.setValue(`https://${trimmedUrl}`);
            }
        }
    }

    prependHttpsToSeedUrl(index: number): void {
        const urlControl = this.seedUrls.at(index).get('url');
        if (urlControl && typeof urlControl.value === 'string') {
            const trimmedUrl = urlControl.value.trim();
            urlControl.setValue(trimmedUrl);

            if (!(trimmedUrl.indexOf('.') === -1) && !trimmedUrl.startsWith('http')) {
                urlControl.setValue(`https://${trimmedUrl}`);
            }
        }
    }

    private _urlValid(url: string): boolean {
        if (url.indexOf('.') === -1) return false;
        if (url.indexOf('://') === -1) return false;
        if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
        if (url.indexOf('://') !== url.lastIndexOf('://')) return false;
        return true;
    }

    private optionalUrlValidator = (control: AbstractControl): ValidationErrors | null => {
        const value = (control.value || '').toString().trim();
        if (value.length === 0) return null;
        return this._urlValid(value) ? null : {invalidUrl: true};
    };

    existingURLValid(idx: number): boolean {
        if (idx >= this.urls.length) return false;

        const url = this.urls.at(idx).get('url');
        if (!url || (url.value || '').trim().length === 0) return false;

        return this._urlValid(url.value.trim());
    }

    allURLsValid(): boolean {
        if (!this.urls || this.urls.length === 0) return false;

        let atLeastOneValid = false;
        for (let i = 0; i < this.urls.length; ++i) {
            const urlValue = this.urls.at(i).get('url')?.value?.trim() || '';
            if (!this.existingURLValid(i)) {
                if (atLeastOneValid && urlValue.length === 0) {
                    continue;
                }
                return false;
            }
            atLeastOneValid = true;
        }
        return true;
    }

    existingSeedURLValid(idx: number): boolean {
        if (idx >= this.seedUrls.length) return false;

        const url = this.seedUrls.at(idx).get('url');
        if (!url || (url.value || '').trim().length === 0) return false;

        return this._urlValid(url.value.trim());
    }

    allSeedURLsValid(): boolean {
        if (!this.seedUrls || this.seedUrls.length === 0) return false;

        let atLeastOneValid = false;
        for (let i = 0; i < this.seedUrls.length; ++i) {
            const urlValue = this.seedUrls.at(i).get('url')?.value?.trim() || '';
            if (!this.existingSeedURLValid(i)) {
                if (atLeastOneValid && urlValue.length === 0) {
                    continue;
                }
                return false;
            }
            atLeastOneValid = true;
        }
        return true;
    }

    addUrlListItem() {
        this.urls.push(this.createUrlGroup());
    }

    removeUrlListItem(index: number) {
        this.urls.removeAt(index);
    }

    clearUrl(index: number) {
        this.urls.at(index).setValue({url: ''});
    }

    addSeedUrlListItem() {
        this.seedUrls.push(this.createUrlGroup());
    }

    removeSeedUrlListItem(index: number) {
        this.seedUrls.removeAt(index);
    }

    clearSeedUrl(index: number) {
        this.seedUrls.at(index).setValue({url: ''});
    }

    trackByIndex(index: number, obj: any): any {
        return index;
    }

    private createUrlGroup(data?: { url?: string }) {
        return this.fb.group({
            url: [data?.url || '', [this.optionalUrlValidator]]
        });
    }

    private loadKnownTestObjects(): void {
        this.testService.getTestObjectsInProject$(this.config.projectId).subscribe({
            next: testObjects => {
                Object.keys(this.knownTestObjectsById).forEach(key => delete this.knownTestObjectsById[key]);
                (testObjects || []).forEach(testObject => {
                    this.knownTestObjectsById[testObject.id] = testObject;
                });
                this.testObjectOptions = (testObjects || []).map(testObject => this.toTestObjectOption(testObject));
                this.rebuildTestObjectCollectionOptions();
            },
            error: () => {
                this.testObjectOptions = [];
                Object.keys(this.knownTestObjectsById).forEach(key => delete this.knownTestObjectsById[key]);
                this.rebuildTestObjectCollectionOptions();
            }
        });
    }

    private loadActiveTestObjectCollections(): void {
        this.testService.getTestObjectCollectionsInProject$(this.config.projectId).subscribe({
            next: collections => {
                this.activeTestObjectCollections = (collections || []).filter(collection => collection.status === 'active');
                this.rebuildTestObjectCollectionOptions();
                this.loadCollectionMembers(this.activeTestObjectCollections.map(collection => collection.collectionId));
            },
            error: () => {
                this.activeTestObjectCollections = [];
                this.testObjectCollectionOptions = [];
            }
        });
    }

    private loadMobilePackages(): void {
        this.packageService.getPackagesForProject(this.config.projectId).pipe(take(1)).subscribe({
            next: packages => {
                this.mobilePackages = (packages || []).filter(pkg => this.isSupportedMobilePackage(pkg));
                this.mobilePackageGroups = this.buildMobilePackageGroups(this.mobilePackages);
            },
            error: () => {
                this.mobilePackages = [];
                this.mobilePackageGroups = [];
            }
        });
    }

    private isSupportedMobilePackage(pkg: VirtualPackage): boolean {
        return !!pkg.origPackageId && (pkg.origPackageType === 'apk' || pkg.origPackageType === 'ipa');
    }

    private buildMobilePackageGroups(packages: VirtualPackage[]): Array<{label: string, items: VirtualPackage[]}> {
        const groups = [
            {label: 'Android', items: packages.filter(pkg => pkg.origPackageType === 'apk')},
            {label: 'iOS', items: packages.filter(pkg => pkg.origPackageType === 'ipa')},
        ];
        return groups.filter(group => group.items.length > 0);
    }

    get selectedMobilePackage(): VirtualPackage | undefined {
        const packageId = this.form?.get('mobilePackageId')?.value;
        return this.mobilePackages.find(pkg => pkg.origPackageId === packageId);
    }

    selectExistingMobilePackage(): void {
        const selectedPackage = this.selectedMobilePackage;
        if (selectedPackage && !this.form.get('mobileName')?.value?.toString().trim()) {
            this.form.patchValue({mobileName: selectedPackage.name});
        }
        this.form.updateValueAndValidity();
    }

    mobilePackagePlatform(pkg: VirtualPackage): string {
        return pkg.origPackageType === 'apk' ? 'Android' : 'iOS';
    }

    openAddMobileAppTool(): void {
        const config = new AppUploadToolConfig(this.config.projectId, undefined, undefined,
            (createdPackage: VirtualPackage) => this.onMobilePackageCreated(createdPackage));
        this.toolService.openTool(AppUploadToolComponent, config);
    }

    private onMobilePackageCreated(createdPackage: VirtualPackage): void {
        if (!this.isSupportedMobilePackage(createdPackage)) {
            return;
        }
        // Insert the fresh package immediately
        if (!this.mobilePackages.some(pkg => pkg.origPackageId === createdPackage.origPackageId)) {
            this.mobilePackages = [createdPackage, ...this.mobilePackages];
            this.mobilePackageGroups = this.buildMobilePackageGroups(this.mobilePackages);
        }
        this.form.patchValue({
            mobilePackageId: createdPackage.origPackageId,
            mobileName: createdPackage.name,
        });
        this.form.updateValueAndValidity();
    }

    private loadCollectionMembers(collectionIds: TestObjectCollectionId[]): void {
        Object.keys(this.loadedTestObjectCollectionsById).forEach(key => delete this.loadedTestObjectCollectionsById[key]);

        if (collectionIds.length === 0) {
            this.loadingTestObjectCollectionMembers = false;
            this.rebuildTestObjectCollectionOptions();
            return;
        }

        this.loadingTestObjectCollectionMembers = true;

        forkJoin(collectionIds.map(collectionId => this.testService.getTestObjectCollection$(collectionId).pipe(
            take(1),
            catchError(() => of(undefined as TestObjectCollection | undefined))
        ))).subscribe(collections => {
            (collections || []).forEach(collection => {
                if (collection) {
                    this.loadedTestObjectCollectionsById[collection.collectionId] = collection;
                }
            });
            this.loadingTestObjectCollectionMembers = false;
            this.rebuildTestObjectCollectionOptions();
        });
    }

    private rebuildTestObjectCollectionOptions(): void {
        this.testObjectCollectionOptions = this.activeTestObjectCollections.map(collection => this.toTestObjectCollectionOption(collection));
    }

    private toTestObjectOption(testObject: TestObject): TestObjectOption {
        const canonicalIdentifier = getTestObjectCanonicalIdentifier(testObject);
        return {
            label: testObject.name || canonicalIdentifier || testObject.id,
            value: testObject.id,
            secondary: canonicalIdentifier
        };
    }

    private toTestObjectCollectionOption(collection: TestObjectCollectionSummary): TestObjectCollectionOption {
        const memberLabel = collection.memberCount === 1 ? '1 member' : `${collection.memberCount} members`;
        const memberLabels = this.getCollectionMemberLabels(collection.collectionId);
        const previewMembers = memberLabels.slice(0, MAX_COLLECTION_OPTION_PREVIEW_MEMBERS);
        const previewOverflowCount = Math.max(memberLabels.length - previewMembers.length, 0);

        return {
            label: collection.name,
            value: collection.collectionId,
            secondary: memberLabel,
            previewSummary: previewMembers.length > 0
                ? `${previewMembers.join(', ')}${previewOverflowCount > 0 ? `, +${previewOverflowCount} more` : ''}`
                : undefined,
            memberLabels
        };
    }

    get selectedTestObjectCollectionOption(): TestObjectCollectionOption | undefined {
        const selectedCollectionId = this.form?.get('selectedTestObjectCollectionId')?.value;
        return this.testObjectCollectionOptions.find(option => option.value === selectedCollectionId);
    }

    get selectedTestObjectCollectionMemberLabels(): string[] {
        return [...(this.selectedTestObjectCollectionOption?.memberLabels || [])];
    }

    get visibleSelectedTestObjectCollectionMemberLabels(): string[] {
        const memberLabels = this.selectedTestObjectCollectionMemberLabels;
        if (this.showAllSelectedCollectionMembers) {
            return memberLabels;
        }

        return memberLabels.slice(0, MAX_SELECTED_COLLECTION_VISIBLE_MEMBERS);
    }

    get selectedTestObjectCollectionMemberOverflowCount(): number {
        if (this.showAllSelectedCollectionMembers) {
            return 0;
        }

        return Math.max(this.selectedTestObjectCollectionMemberLabels.length - this.visibleSelectedTestObjectCollectionMemberLabels.length, 0);
    }

    selectedTestObjectCollectionMembersLoading(): boolean {
        const selectedCollectionId = this.form?.get('selectedTestObjectCollectionId')?.value;
        return !!selectedCollectionId
            && this.loadingTestObjectCollectionMembers
            && !this.loadedTestObjectCollectionsById[selectedCollectionId];
    }

    toggleSelectedTestObjectCollectionMembers(): void {
        this.showAllSelectedCollectionMembers = !this.showAllSelectedCollectionMembers;
    }

    private hasSelectedKnownTestObjects(control?: AbstractControl): boolean {
        return (((control || this.form)?.get('selectedTestObjectIds')?.value || []) as string[]).length > 0;
    }

    private hasSelectedTestObjectCollection(control?: AbstractControl): boolean {
        return !!(control || this.form)?.get('selectedTestObjectCollectionId')?.value;
    }

    formatKnownTestObjectsSelectionLabel(): string {
        const count = ((this.form.get('selectedTestObjectIds')?.value || []) as string[]).length;
        if (count === 0) {
            return 'No Test Objects selected';
        }
        if (count === 1) {
            return '1 Test Object selected';
        }
        return `${count} Test Objects selected`;
    }

    reset() {
        this.manualLabels = [];
        const formParams = {
            sourceType: this.config.lastShownTool ?? 'urllist',
            urls: [{url: ''}],
            selectedTestObjectIds: [],
            selectedTestObjectCollectionId: null,
            manualName: '',
            manualDescription: '',
            manualInstructions: '',
            manualMatchingMode: 'manualOnly',
            manualMatchingUrl: '',
            manualMatchingRegex: '',
            mobileName: '',
            mobileDescription: '',
            mobilePackageId: null,
            seedUrls: [{url: ''}],
            crawlAdvancedSetup: false,
            crawlUseSitemap: false,
            crawlMaxStates: 25,
            crawlMaxDepth: 3,
            crawlMaxWidth: 8,
            crawlMaxCrawlDuration: '5 minutes',
            crawlPrioritizingStrategy: 'BreadthFirst',
            crawlRestrictToSeedHosts: true,
            crawlUseAnchorTags: true,
            crawlUseFormActions: false,
            crawlUseClickableElements: false,
        };

        this.form = this.fb.group({
            sourceType: [formParams.sourceType],
            urls: this.fb.array(formParams.urls.map(urlObj => this.createUrlGroup(urlObj)), Validators.required),
            selectedTestObjectIds: [formParams.selectedTestObjectIds],
            selectedTestObjectCollectionId: [formParams.selectedTestObjectCollectionId],
            manualName: [formParams.manualName],
            manualDescription: [formParams.manualDescription],
            manualInstructions: [formParams.manualInstructions],
            manualMatchingMode: [formParams.manualMatchingMode],
            manualMatchingUrl: [formParams.manualMatchingUrl, [this.optionalUrlValidator]],
            manualMatchingRegex: [formParams.manualMatchingRegex],
            mobileName: [formParams.mobileName],
            mobileDescription: [formParams.mobileDescription],
            mobilePackageId: [formParams.mobilePackageId],
            seedUrls: this.fb.array(formParams.seedUrls.map(urlObj => this.createUrlGroup(urlObj)), Validators.required),
            crawlAdvancedSetup: [formParams.crawlAdvancedSetup],
            crawlUseSitemap: [formParams.crawlUseSitemap],
            crawlMaxStates: [formParams.crawlMaxStates, Validators.min(1)],
            crawlMaxDepth: [formParams.crawlMaxDepth, Validators.min(1)],
            crawlMaxWidth: [formParams.crawlMaxWidth, Validators.min(1)],
            crawlMaxCrawlDuration: [formParams.crawlMaxCrawlDuration, Validators.required],
            crawlPrioritizingStrategy: [formParams.crawlPrioritizingStrategy],
            crawlRestrictToSeedHosts: [formParams.crawlRestrictToSeedHosts],
            crawlUseAnchorTags: [formParams.crawlUseAnchorTags],
            crawlUseFormActions: [formParams.crawlUseFormActions],
            crawlUseClickableElements: [formParams.crawlUseClickableElements],
        }, {
            validators: [
                (control: AbstractControl): ValidationErrors | null => {
                    const sourceType = control.get('sourceType');
                    if (sourceType && sourceType.value === 'urllist') {
                        if (!this.urls || this.urls.length === 0) {
                            return {urlsRequired: true};
                        } else if (!this.allURLsValid()) {
                            return {urlsInvalid: true};
                        } else {
                            return null;
                        }
                    } else if (sourceType && sourceType.value === 'testobjectlist') {
                        if (!this.hasSelectedKnownTestObjects(control)) {
                            return {selectedTestObjectIdsRequired: true};
                        } else {
                            return null;
                        }
                    } else if (sourceType && sourceType.value === 'testobjectcollection') {
                        if (!this.hasSelectedTestObjectCollection(control)) {
                            return {selectedTestObjectCollectionIdRequired: true};
                        } else {
                            return null;
                        }
                    } else if (sourceType?.value === 'manual') {
                        if (!control.get('manualName')?.value?.toString().trim()) {
                            return {manualNameRequired: true};
                        } else if (!control.get('manualInstructions')?.value?.toString().trim()) {
                            return {manualInstructionsRequired: true};
                        }

                        const matchingMode = control.get('manualMatchingMode')?.value;
                        if (matchingMode === 'exactUrl' && !this._urlValid((control.get('manualMatchingUrl')?.value || '').trim())) {
                            return {manualMatchingUrlInvalid: true};
                        }

                        if (matchingMode === 'urlRegex') {
                            const pattern = (control.get('manualMatchingRegex')?.value || '').toString().trim();
                            if (!pattern) {
                                return {manualMatchingRegexRequired: true};
                            }
                            try {
                                // validate regex syntax without storing the compiled instance
                                new RegExp(pattern);
                            } catch (e) {
                                return {manualMatchingRegexInvalid: true};
                            }
                        }

                        return null;
                    } else if (sourceType?.value === 'mobileapp') {
                        if (!control.get('mobileName')?.value?.toString().trim()) {
                            return {mobileNameRequired: true};
                        }
                        if (!control.get('mobilePackageId')?.value) {
                            return {mobilePackageRequired: true};
                        }
                        return null;
                    } else if (sourceType && sourceType.value === 'crawled') {
                        if (!this.seedUrls || this.seedUrls.length === 0) {
                            return {seedUrlsRequired: true};
                        } else if (!this.allSeedURLsValid()) {
                            return {seedUrlsInvalid: true};
                        } else if (!control.get('crawlMaxCrawlDuration')?.value?.toString().trim()) {
                            return {crawlMaxCrawlDurationRequired: true};
                        } else {
                            return null;
                        }
                    }

                    return null;
                }
            ]
        });

        this.form.get('sourceType')!.valueChanges.subscribe((value: TestObjectsSourceType) => {
            this.config.lastShownTool = value;
        });
        this.form.get('selectedTestObjectCollectionId')!.valueChanges.subscribe(() => {
            this.showAllSelectedCollectionMembers = false;
        });
    }

    cancel() {
        this.toolService.closeLastTool();
    }

    confirm() {
        if (!this.form.valid || this.confirming) {
            return;
        }
        this.confirming = true;

        if ((this.form.get('sourceType')?.value as TestObjectsSourceType) === 'manual') {
            const dto = this.createManualTestObjectDTO();
            this.testService.createTestObject$(this.config.projectId, dto).pipe(take(1)).subscribe({
                next: testObjectId => {
                    const sourceSpec: TestObjectListSourceSpec = {
                        type: 'TestObjectListSourceSpec',
                        testObjectIds: [testObjectId],
                    };
                    this.config.onOk(sourceSpec, {directlyCreatedTestObjectIds: [testObjectId]});
                    if (this.shouldCloseAfterOk()) {
                        this.toolService.closeLastTool();
                    }
                },
                error: err => {
                    this.confirming = false;
                    console.error('Failed to create manual test object', err);
                    this.notificationService.errorNotification('Could not create manual Test Object.');
                }
            });
            return;
        }

        if ((this.form.get('sourceType')?.value as TestObjectsSourceType) === 'mobileapp') {
            const pkg = this.selectedMobilePackage;
            if (!pkg) {
                this.confirming = false;
                return;
            }
            this.testService.createTestObject$(this.config.projectId, this.createMobileTestObjectDTO(pkg)).pipe(take(1)).subscribe({
                next: testObjectId => {
                    const sourceSpec: TestObjectListSourceSpec = {
                        type: 'TestObjectListSourceSpec',
                        testObjectIds: [testObjectId],
                    };
                    this.config.onOk(sourceSpec, {directlyCreatedTestObjectIds: [testObjectId]});
                    if (this.shouldCloseAfterOk()) {
                        this.toolService.closeLastTool();
                    }
                },
                error: err => {
                    this.confirming = false;
                    console.error('Failed to create mobile application test object', err);
                    this.notificationService.errorNotification(
                        'Could not create the mobile app Test Object. Check that the package has valid launch metadata.');
                }
            });
            return;
        }

        const sourceSpec = this.createSourceSpecFromForm();
        this.config.onOk(sourceSpec);
        if (this.shouldCloseAfterOk()) {
            this.toolService.closeLastTool();
        }
    }

    private createSourceSpecFromForm(): TestObjectsSourceSpec {
        const sourceType = this.form.get('sourceType')?.value as TestObjectsSourceType;
        if (sourceType === 'crawled') {
            const seedUrls = this.seedUrls.controls
                .map(control => (control.get('url')?.value || '').trim())
                .filter(url => this._urlValid(url))
                .map(url => ({url}));

            const sourceSpec: CrawledSourceSpec = {
                type: 'CrawledSourceSpec',
                seed: {
                    urls: seedUrls,
                    useSitemap: !!this.form.get('crawlUseSitemap')?.value
                },
                config: {
                    maxStates: this.optionalPositiveNumber(this.form.get('crawlMaxStates')?.value),
                    maxDepth: this.optionalPositiveNumber(this.form.get('crawlMaxDepth')?.value),
                    maxWidth: this.optionalPositiveNumber(this.form.get('crawlMaxWidth')?.value),
                    maxCrawlDuration: (this.form.get('crawlMaxCrawlDuration')?.value || '5 minutes').toString().trim(),
                    prioritizingStrategy: this.form.get('crawlPrioritizingStrategy')?.value || 'BreadthFirst',
                    explorationStrategy: {
                        restrictToSeedHosts: !!this.form.get('crawlRestrictToSeedHosts')?.value,
                        useAnchorTags: !!this.form.get('crawlUseAnchorTags')?.value,
                        useFormActions: !!this.form.get('crawlUseFormActions')?.value,
                        useClickableElements: !!this.form.get('crawlUseClickableElements')?.value,
                    }
                }
            };
            return sourceSpec;
        } else if (sourceType === 'testobjectlist') {
            const sourceSpec: TestObjectListSourceSpec = {
                type: 'TestObjectListSourceSpec',
                testObjectIds: [...((this.form.get('selectedTestObjectIds')?.value || []) as string[])]
            };
            return sourceSpec;
        } else if (sourceType === 'testobjectcollection') {
            const sourceSpec: TestObjectCollectionSourceSpec = {
                type: 'TestObjectCollectionSourceSpec',
                specCollectionId: this.form.get('selectedTestObjectCollectionId')?.value
            };
            return sourceSpec;
        }

        const urls = this.urls.controls
            .map(control => (control.get('url')?.value || '').trim())
            .filter(url => this._urlValid(url));
        const sourceSpec: URLListSourceSpec = {
            type: 'URLListSourceSpec',
            urls: urls
        };

        return sourceSpec;
    }

    private createManualTestObjectDTO(): CreateTestObjectDTO {
        const matchingMode = this.form.get('manualMatchingMode')?.value;
        const trimmedDescription = (this.form.get('manualDescription')?.value || '').toString().trim();
        const trimmedInstructions = (this.form.get('manualInstructions')?.value || '').toString().trim();
        const trimmedMatchingUrl = (this.form.get('manualMatchingUrl')?.value || '').toString().trim();
        const trimmedMatchingRegex = (this.form.get('manualMatchingRegex')?.value || '').toString().trim();

        const generationSpec: ManualGenerationSpec = {
            type: 'ManualGenerationSpec',
            instructions: trimmedInstructions,
        };

        let matchingSpec: NoAutomaticMatchingSpec | URLMatchingSpec | undefined;
        if (matchingMode === 'exactUrl' && trimmedMatchingUrl) {
            matchingSpec = {
                type: 'URLMatchingSpec',
                urlRegex: trimmedMatchingUrl,
            };
        } else if (matchingMode === 'urlRegex' && trimmedMatchingRegex) {
            matchingSpec = {
                type: 'URLMatchingSpec',
                urlRegex: trimmedMatchingRegex,
            };
        } else {
            matchingSpec = {
                type: 'NoAutomaticMatchingSpec',
            };
        }

        return {
            name: (this.form.get('manualName')?.value || '').toString().trim(),
            description: trimmedDescription || undefined,
            generationSpec,
            matchingSpec,
            labels: [...this.manualLabels],
        };
    }

    private createMobileTestObjectDTO(pkg: VirtualPackage): CreateTestObjectDTO {
        const generationSpec: MobileAppGenerationSpec = {
            type: 'MobileAppGenerationSpec',
            packageId: pkg.origPackageId,
        };

        return {
            name: (this.form.get('mobileName')?.value || pkg.name).toString().trim(),
            description: (this.form.get('mobileDescription')?.value || '').toString().trim() || undefined,
            generationSpec,
            matchingSpec: {type: 'NoAutomaticMatchingSpec'},
            labels: [],
        };
    }

    private optionalPositiveNumber(value: any): number | undefined {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        const numberValue = Number(value);
        if (isNaN(numberValue) || numberValue <= 0) {
            return undefined;
        }

        return numberValue;
    }

    private shouldCloseAfterOk(): boolean {
        return this.config.postCreatePolicyConfiguration === 'none';
    }

    private getCollectionMemberLabels(collectionId: TestObjectCollectionId): string[] {
        const collection = this.loadedTestObjectCollectionsById[collectionId];
        if (!collection) {
            return [];
        }

        return (collection.members || []).map(memberRef => this.formatCollectionMemberLabel(memberRef));
    }

    private formatCollectionMemberLabel(memberRef: TestObjectCollectionMemberRef): string {
        const testObjectId = getTestObjectCollectionMemberBaseId(memberRef);
        const testObject = this.knownTestObjectsById[testObjectId];
        const memberLabel = getTestObjectCollectionMemberLabel(memberRef);
        const testObjectLabel = testObject ? this.formatTestObjectLabel(testObject) : testObjectId;

        return memberLabel === 'Latest' ? testObjectLabel : `${testObjectLabel} - ${memberLabel}`;
    }

    private formatTestObjectLabel(testObject: TestObject): string {
        const name = testObject.name?.trim();
        const canonicalIdentifier = getTestObjectCanonicalIdentifier(testObject)?.trim();
        if (name && canonicalIdentifier && name !== canonicalIdentifier) {
            return `${name} (${canonicalIdentifier})`;
        }

        return name || canonicalIdentifier || testObject.id;
    }
}
